import { Trans, useLingui } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { type ColorPaletteProp } from '@mui/joy/styles';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { ListItemDecorator } from '@startuptools/ui/ListItemDecorator';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Option } from '@startuptools/ui/Option';
import { Select } from '@startuptools/ui/Select';
import { Table } from '@startuptools/ui/Table';
import { Stack } from '@startuptools/ui/Stack';
import { type SelectOption } from '@mui/base';
import { Snackbar } from '@startuptools/ui/Snackbar';
import { GenericError } from '@startuptools/ui/Error';
import { useMemo, useState } from 'react';
import { DisplayIdentity, Identity } from '@startuptools/business-ui/identity/Identity';
import { isBoolean, isNil, isObject } from 'lodash-es';
import { PaymentKind, PaymentStatus } from '@startuptools/common/participants';
import {
  useDeleteShareIssueParticipantMutation,
  useSipSetPaymentStatusMutation,
  useSipSetProxyMutation,
} from '../../../graphql/react-operations';
import { captureException } from '@sentry/react';
import { GqlShareClass, GqlShareIssue, type GqlShareIssueParticipant } from '../../../graphql/base-types.graphql';
import { BigNumber } from 'bignumber.js';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Tooltip } from '@startuptools/ui/Tooltip';
import { ShareIssueParticipantDialog } from './ShareIssueParticipantDialog';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { DetailState, InputState, JuridicalKind } from '@startuptools/business-ui/identity/types';
import { AlertConfirmDialog } from '../../../components/simple-dialogs/AlertConfirmDialog';
import { ShareIssueDocument } from '../ShareIssue.graphql';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { PaymentKindHuman } from '../../../components/payment-kind-human/PaymentKindHuman';

const paymentStatusColor: Record<PaymentStatus, ColorPaletteProp> = {
  [PaymentStatus.Paid]: 'success',
  [PaymentStatus.WaitingForPayment]: 'neutral',
  [PaymentStatus.NotPaid]: 'danger',
};

const PaymentStatusIcon = ({ children }: { children: PaymentStatus }) => {
  switch (children) {
    case PaymentStatus.Paid: {
      return <CheckIcon color="success" />;
    }
    case PaymentStatus.WaitingForPayment: {
      return <HourglassEmptyIcon />;
    }
    case PaymentStatus.NotPaid: {
      return <CloseIcon color="error" />;
    }
  }
};

const renderValue = (option: SelectOption<PaymentStatus> | null) => {
  if (!option) {
    return null;
  }

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <ListItemDecorator>
        <PaymentStatusIcon>{option.value}</PaymentStatusIcon>
      </ListItemDecorator>
      {option.label}
    </Stack>
  );
};

export const ParticipantsTable = ({
  shareIssue,
  sirId,
  sips,
  paymentKind,
}: {
  shareIssue: Pick<
    GqlShareIssue,
    'id' | 'pricePerShare' | 'currency' | 'companyId' | 'maxNumberOfShares' | 'shareClasses' | 'paymentKindCash'
  >;
  sirId: string;
  sips: (Pick<
    GqlShareIssueParticipant,
    'id' | 'paymentStatus' | 'numberOfShares' | 'shareClass' | 'paymentKind' | 'claim'
  > & {
    identity: DisplayIdentity;
    proxy?: DisplayIdentity | null;
  })[];
  paymentKind: PaymentKind | null;
}) => {
  const { i18n } = useLingui();
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [showParticipantDialog, setShowParticipantDialog] = useState<{
    id: string | null;
    shareClass: GqlShareClass | null;
    numberOfShares: string | null;
    claim: string | null;
    identity: DisplayIdentity;
  } | null>(null);
  const [showAddIdentity, setShowAddIdentity] = useState<boolean | { id: string; identityNumber: string } | null>(null);
  const [showDelete, setShowDelete] = useState<string | null>(null);
  const [doDelete, { loading: deleteLoading }] = useDeleteShareIssueParticipantMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });

  const showPayment = isNil(paymentKind);
  const showActions = !isNil(paymentKind);
  const showRepresentative = paymentKind === PaymentKind.Cash;

  const [doSetPaymentStatus, { loading }] = useSipSetPaymentStatusMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetProxy] = useSipSetProxyMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });

  const pricePerShare = shareIssue.pricePerShare ?? 0;

  const { paid, total, totalAmount, totalAmountPaid } = sips.reduce(
    (prev, sip) => {
      // the amount each sip pays is rounded by 2 decimals on purpose because in the real world we can only pay with 2 decimals
      return {
        paid: prev.paid + (sip.paymentStatus === PaymentStatus.Paid ? Number(sip.numberOfShares ?? 0) : 0),
        total: prev.total + (sip.paymentStatus !== PaymentStatus.NotPaid ? Number(sip.numberOfShares ?? 0) : 0),
        totalAmountPaid:
          prev.totalAmountPaid +
          (sip.paymentStatus === PaymentStatus.Paid
            ? Number(new BigNumber(sip.numberOfShares ?? 0).times(pricePerShare).toFixed(2))
            : 0),
        totalAmount:
          prev.totalAmount +
          (sip.paymentStatus !== PaymentStatus.NotPaid
            ? Number(new BigNumber(sip.numberOfShares ?? 0).times(pricePerShare).toFixed(2))
            : 0),
      };
    },
    { paid: 0, total: 0, totalAmount: 0, totalAmountPaid: 0 },
  );

  const handleOnChange = async (sip: Pick<GqlShareIssueParticipant, 'id'>, value: number | null) => {
    if (isNil(value)) {
      return;
    }
    try {
      await doSetPaymentStatus({
        variables: { companyId: shareIssue.companyId, input: { id: sip.id, paymentStatus: value! } },
      });
    } catch (e) {
      captureException(e);
      setShowErrorToast(true);
    }
  };

  const totals = useMemo(() => {
    const sipsByGroup = Object.groupBy(sips, sip => sip.paymentKind);
    const cash = sipsByGroup[PaymentKind.Cash]?.reduce((sum, e) => sum + Number(e.numberOfShares ?? 0), 0);
    const inKind = sipsByGroup[PaymentKind.NonCash]?.reduce((sum, e) => sum + Number(e.numberOfShares ?? 0), 0);
    const offset = sipsByGroup[PaymentKind.Offset]?.reduce((sum, e) => sum + Number(e.numberOfShares ?? 0), 0);
    const claim = sipsByGroup[PaymentKind.Offset]?.reduce((sum, e) => sum + Number(e.claim ?? 0), 0);
    const sum = (cash ?? 0) + (inKind ?? 0) + (offset ?? 0);
    const overallocated = shareIssue.maxNumberOfShares ? sum - Number(shareIssue.maxNumberOfShares) : 0;

    return {
      cash,
      inKind,
      offset,
      claim,
      sum,
      overallocated: overallocated > 0 ? overallocated : null,
    };
  }, [sips, shareIssue.maxNumberOfShares]);

  return (
    <>
      <Stack gap={3}>
        {!isNil(paymentKind) && (paymentKind !== PaymentKind.Cash || shareIssue.paymentKindCash) && (
          <Box>
            <Button variant="outlined" onClick={() => setShowAddIdentity(true)}>
              <Trans id="Add subscriber" />
            </Button>
          </Box>
        )}

        <Card>
          <Table hoverRow sx={{ tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th>
                  <Trans id="Buyer" />
                </th>
                {showRepresentative && (
                  <th>
                    <Trans id="Representative" />
                  </th>
                )}
                <th>
                  <Trans id="Share Class" />
                </th>
                <th align="right">
                  <Trans id="Number of Shares" />
                </th>
                <th align="right">
                  {paymentKind === PaymentKind.Offset ? <Trans id="Claim" /> : <Trans id="Amount to Pay" />}
                </th>
                <th>
                  <Trans id="Payment Method" />
                </th>
                {showPayment && (
                  <th>
                    <Trans id="Payment Status" />
                  </th>
                )}
                {showActions && <th align="right" />}
              </tr>
            </thead>
            <tbody>
              {sips.map(sip => {
                const notPaid = sip.paymentStatus === PaymentStatus.NotPaid;
                return (
                  <tr
                    key={sip.id}
                    style={{
                      textDecoration: notPaid ? 'line-through' : undefined,
                      color: notPaid ? 'grey' : undefined,
                    }}
                  >
                    <td>
                      <Identity identity={sip.identity} editable={false} />
                    </td>
                    {showRepresentative && (
                      <td>
                        {sip.proxy ? (
                          <Stack direction="row" gap={1}>
                            <Identity identity={sip.proxy} editable={false} />
                            <Tooltip title={<Trans id="Replace Representative" />}>
                              <IconButton
                                onClick={() =>
                                  setShowAddIdentity({ id: sip.id, identityNumber: sip.identity.identityNumber })
                                }
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <>
                            {sip.identity.juridicalKind === JuridicalKind.Company && (
                              <Stack direction="row" gap={1} alignItems="center">
                                <WarningAmberIcon color="warning" fontSize="small" />
                                <Button
                                  size="sm"
                                  variant="outlined"
                                  color="neutral"
                                  onClick={() =>
                                    setShowAddIdentity({ id: sip.id, identityNumber: sip.identity.identityNumber })
                                  }
                                >
                                  <Trans id="Set Representative" />
                                </Button>
                              </Stack>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    <td>{sip.shareClass?.name}</td>
                    <td align="right">
                      <NumberFormat locale={i18n.locale} variant="shares">
                        {sip.numberOfShares}
                      </NumberFormat>
                    </td>
                    <td align="right">
                      <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                        {sip.numberOfShares
                          ? paymentKind !== PaymentKind.Offset
                            ? new BigNumber(sip.numberOfShares).times(shareIssue.pricePerShare ?? 0)
                            : sip.claim
                          : null}
                      </NumberFormat>
                    </td>
                    <td>
                      <PaymentKindHuman>{sip.paymentKind}</PaymentKindHuman>
                    </td>
                    {showPayment && (
                      <td style={{ width: 220 }}>
                        <Select
                          disabled={loading || sip.paymentKind !== PaymentKind.Cash}
                          variant={sip.paymentKind !== PaymentKind.Cash ? 'plain' : 'outlined'}
                          color={paymentStatusColor[sip.paymentStatus as unknown as PaymentStatus]}
                          value={sip.paymentStatus}
                          renderValue={renderValue}
                          onChange={(_, value) => handleOnChange(sip, value)}
                        >
                          <Option value={PaymentStatus.Paid}>
                            <ListItemDecorator>
                              <PaymentStatusIcon>{PaymentStatus.Paid}</PaymentStatusIcon>
                            </ListItemDecorator>
                            <Trans id="Has Paid" />
                          </Option>
                          <Option value={PaymentStatus.WaitingForPayment}>
                            <ListItemDecorator>
                              <PaymentStatusIcon>{PaymentStatus.WaitingForPayment}</PaymentStatusIcon>
                            </ListItemDecorator>
                            <Trans id="Waiting for Payment" />
                          </Option>
                          <Option value={PaymentStatus.NotPaid}>
                            <ListItemDecorator>
                              <PaymentStatusIcon>{PaymentStatus.NotPaid}</PaymentStatusIcon>
                            </ListItemDecorator>
                            <Trans id="Payment Overdue" />
                          </Option>
                        </Select>
                      </td>
                    )}
                    {showActions && (
                      <td align="right">
                        <Tooltip
                          title={
                            sip.paymentKind !== paymentKind ? (
                              sip.paymentKind === PaymentKind.Offset ? (
                                <Trans id="Go back to step offset to edit" />
                              ) : (
                                <Trans id="Go back to step in-kind to edit" />
                              )
                            ) : null
                          }
                        >
                          <span>
                            <ButtonGroup variant="outlined" disabled={sip.paymentKind !== paymentKind}>
                              <IconButton
                                color="primary"
                                onClick={() =>
                                  setShowParticipantDialog({
                                    id: sip.id,
                                    shareClass: sip.shareClass ?? null,
                                    numberOfShares: sip.numberOfShares ?? null,
                                    claim: sip.claim ?? null,
                                    identity: sip.identity,
                                  })
                                }
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton color="danger" onClick={() => setShowDelete(sip.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </ButtonGroup>
                          </span>
                        </Tooltip>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {showPayment ? (
                <tr>
                  <th colSpan={2}>
                    <Trans id="Approved Payments:" />
                  </th>
                  <th align="right">
                    <NumberFormat locale={i18n.locale} variant="shares">
                      {paid}
                    </NumberFormat>
                    {' / '}
                    <NumberFormat locale={i18n.locale} variant="shares">
                      {total}
                    </NumberFormat>
                  </th>
                  <th align="right">
                    <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                      {totalAmountPaid}
                    </NumberFormat>
                    {' / '}
                    <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                      {totalAmount}
                    </NumberFormat>
                  </th>
                  <th colSpan={2} />
                </tr>
              ) : (
                <>
                  {!isNil(totals.cash) && (
                    <tr>
                      <th>
                        <Trans id="Total offering with cash payment:" />
                      </th>
                      <th colSpan={paymentKind === PaymentKind.Cash ? 2 : 1} />
                      <th align="right">
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {totals.cash}
                        </NumberFormat>
                      </th>
                      <th align="right">
                        <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                          {new BigNumber(totals.cash).times(pricePerShare)}
                        </NumberFormat>
                      </th>
                      <th colSpan={2} />
                    </tr>
                  )}
                  {!isNil(totals.offset) && (
                    <tr>
                      <th>
                        <Trans id="Total subscribed with offset payment:" />
                      </th>
                      <th colSpan={paymentKind === PaymentKind.Cash ? 2 : 1} />
                      <th align="right">
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {totals.offset}
                        </NumberFormat>
                      </th>
                      <th align="right">
                        <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                          {totals.claim}
                        </NumberFormat>
                      </th>
                      <th colSpan={2} />
                    </tr>
                  )}
                  {!isNil(totals.inKind) && (
                    <tr>
                      <th>
                        <Trans id="Total subscribed with offset payment:" />
                      </th>
                      <th colSpan={paymentKind === PaymentKind.Cash ? 2 : 1} />
                      <th align="right">
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {totals.inKind}
                        </NumberFormat>
                      </th>
                      <th align="right">
                        <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                          {new BigNumber(totals.inKind).times(pricePerShare)}
                        </NumberFormat>
                      </th>
                      <th colSpan={2} />
                    </tr>
                  )}
                  {!isNil(totals.overallocated) && (
                    <tr>
                      <th>
                        <Trans id="Overallocated:" />
                      </th>
                      <th colSpan={paymentKind === PaymentKind.Cash ? 2 : 1} />
                      <th align="right">
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {totals.overallocated}
                        </NumberFormat>
                      </th>
                      <th align="right">
                        <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                          {new BigNumber(totals.overallocated).times(pricePerShare)}
                        </NumberFormat>
                      </th>
                      <th colSpan={2} />
                    </tr>
                  )}
                  {paymentKind === PaymentKind.Cash && !isNil(totals.sum) && (
                    <tr>
                      <th>
                        <Trans id="Total:" />
                      </th>
                      <th colSpan={paymentKind === PaymentKind.Cash ? 2 : 1} />
                      <th align="right">
                        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                          {totals.sum}
                        </NumberFormat>
                      </th>
                      <th align="right">
                        <NumberFormat locale={i18n.locale} variant="currency" currency={shareIssue.currency}>
                          {new BigNumber(totals.sum).times(pricePerShare)}
                        </NumberFormat>
                      </th>
                      <th colSpan={2} />
                    </tr>
                  )}
                </>
              )}
            </tfoot>
          </Table>
        </Card>
        <Snackbar open={showErrorToast} onClose={() => setShowErrorToast(false)}>
          <GenericError color="warning" sx={{ width: '100%' }} />
        </Snackbar>
      </Stack>
      {showParticipantDialog && (
        <ShareIssueParticipantDialog
          sips={sips}
          sip={showParticipantDialog}
          shareIssue={shareIssue}
          onClose={() => setShowParticipantDialog(null)}
          paymentKind={paymentKind!}
          sirId={sirId}
        />
      )}
      {showAddIdentity && (
        <AddIdentity
          headline={<Trans id="Add subscriber" />}
          juridicalKinds={
            isBoolean(showAddIdentity) ? [JuridicalKind.Person, JuridicalKind.Company] : [JuridicalKind.Person]
          }
          availableInputStates={{
            [InputState.System]: isBoolean(showAddIdentity),
            [InputState.CompanyBoard]: isObject(showAddIdentity),
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
          }}
          companyBoardOrgNumber={isObject(showAddIdentity) ? showAddIdentity.identityNumber : undefined}
          detailFieldsSettings={{ email: DetailState.Optional }}
          handleCreate={async identity => {
            if (isBoolean(showAddIdentity)) {
              setShowParticipantDialog({
                id: null,
                numberOfShares: null,
                claim: null,
                shareClass: null,
                identity,
              });
            } else {
              try {
                await doSetProxy({
                  variables: {
                    companyId: shareIssue.companyId,
                    input: { id: showAddIdentity.id, proxyInput: identityToInput(identity) },
                  },
                });
              } catch (e) {
                captureException(e);
                genericErrorToast();
              }
            }
            setShowAddIdentity(null);
          }}
          onClose={() => {
            setShowAddIdentity(null);
          }}
        />
      )}
      {showDelete && (
        <AlertConfirmDialog
          onClose={() => setShowDelete(null)}
          onConfirm={() => doDelete({ variables: { companyId: shareIssue.companyId, id: showDelete } })}
          loading={deleteLoading}
        />
      )}
    </>
  );
};
